import React, { useState } from 'react';

import I18n from 'utils/I18n';
import Cldr from 'utils/Cldr';
import clsx from '@strava/ui/clsx';
import { trackV2 } from 'utils/analytics';

import CreditCardForm from 'components/CreditCardForm';
import Card from '../shared/Card';

import {
  postAdyenProductCheckout,
  postAdyenTrialCheckout
} from '../../state/Checkout.actions';

import visa from './assets/visa.svg';
import mc from './assets/mc.svg';
import discover from './assets/discover.svg';
import amex from './assets/amex.svg';
import paypal from './assets/paypal.svg';

import {
  subscriptionType,
  getStyles as _getStyles
} from '../../utils/checkoutUtils';
import CheckoutError from './CheckoutError';
import PayPalCheckout from './PayPalCheckout';
import { useCheckoutContext } from '../../state/CheckoutContext';

import styles from './Form.scss';

const PAYMENT_METHOD_CREDIT = 'credit';
const PAYMENT_METHOD_PAYPAL = 'paypal';

export const I18nPrefix = 'strava.subscriptions.checkout.form';

export const Form = () => {
  const { checkoutEntityState, dispatch } = useCheckoutContext();

  const {
    adyenClientPubkey,
    analyticsData,
    checkoutError,
    checkoutSubmitting,
    promoCodeSubmitting,
    checkoutSuccess,
    checkoutSuccessRedirectPath,
    isTrial,
    showPaypal,
    isStudentPromotion,
    duration,
    pricing,
    products,
    experiments
  } = checkoutEntityState;

  const { currency, sku, billedToday } = pricing;
  const fullPromo = products?.promotion?.full;
  const promotionCode = products?.promotion?.code;
  const { shouldShowCheckoutRefresh } = experiments;

  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHOD_CREDIT);

  const getStyles = (style) =>
    _getStyles(styles, style, shouldShowCheckoutRefresh);

  const handleOnChange = (method) => {
    if (paymentMethod !== method) {
      setPaymentMethod(method);
    }
  };

  const ctaText = () => {
    if (isTrial) {
      return I18n.t(`${I18nPrefix}.submit_cta.start_trial`);
    }
    if (fullPromo) {
      return I18n.t(`${I18nPrefix}.submit_cta.start_membership`);
    }

    return I18n.t(`${I18nPrefix}.submit_cta.securely_pay`, {
      price: Cldr.formatCurrency(billedToday, currency)
    });
  };

  const handleCreditCardFormSubmit = (encryptCardData) => {
    trackV2({
      category: 'summit_upsell',
      page: 'cart',
      action: 'finish_payment',
      properties: {
        ...analyticsData,
        subscription_type: subscriptionType(isTrial)
      }
    });

    const formData = {
      adyen_card_data: encryptCardData,
      sku,
      promotion: promotionCode,
      duration
    };

    if (isTrial) {
      postAdyenTrialCheckout(formData, checkoutEntityState, dispatch);
    } else {
      postAdyenProductCheckout(formData, checkoutEntityState, dispatch);
    }
  };

  const renderPayment = () => {
    if (checkoutError) {
      return <CheckoutError />;
    }

    if (paymentMethod === PAYMENT_METHOD_CREDIT) {
      return (
        <CreditCardForm
          adyenClientPubkey={adyenClientPubkey}
          submitHandler={handleCreditCardFormSubmit}
          isSubmitting={checkoutSubmitting}
          disabledAll={checkoutSubmitting || promoCodeSubmitting}
          ctaText={ctaText()}
          showRequiredCopy={!fullPromo}
          analyticsData={analyticsData}
          isTrial={isTrial}
          isStudentPromotion={isStudentPromotion}
        />
      );
    }

    return <PayPalCheckout />;
  };

  if (checkoutSuccess) {
    window.location = checkoutSuccessRedirectPath;
  }

  return (
    <div className={styles.checkoutForm}>
      <Card className={clsx(getStyles('card'))}>
        <div className={styles.paymentMethods}>
          <div className={styles.paymentTitle}>
            {I18n.t(`${I18nPrefix}.payment_method`)}
          </div>
          <div className={styles.paymentOptionList}>
            <label htmlFor="credit" className={styles.paymentOption}>
              <input
                id="credit"
                type="radio"
                name="payment-method"
                data-testid="credit"
                className={styles.input}
                checked={paymentMethod === PAYMENT_METHOD_CREDIT}
                onChange={() => handleOnChange(PAYMENT_METHOD_CREDIT)}
                disabled={checkoutSubmitting || promoCodeSubmitting}
              />
              {I18n.t(`${I18nPrefix}.credit_or_debit`)}
              <div className={styles.cardsContainer}>
                <img className={styles.cardImg} src={visa} alt="visa" />
                <img className={styles.cardImg} src={mc} alt="master card" />
                <img className={styles.cardImg} src={discover} alt="discover" />
                <img
                  className={styles.cardImg}
                  src={amex}
                  alt="american express"
                />
              </div>
            </label>
            {showPaypal && (
              <label htmlFor="paypal" className={styles.paymentOption}>
                <input
                  id="paypal"
                  type="radio"
                  name="payment-method"
                  data-testid="paypal"
                  className={styles.input}
                  checked={paymentMethod === PAYMENT_METHOD_PAYPAL}
                  onChange={() => handleOnChange(PAYMENT_METHOD_PAYPAL)}
                  disabled={checkoutSubmitting || promoCodeSubmitting}
                />
                <img className={styles.paypalImg} src={paypal} alt="paypal" />
              </label>
            )}
          </div>
        </div>

        <div className={styles.divider} />

        <div className={styles.payment}>{renderPayment()}</div>
      </Card>
    </div>
  );
};

export default Form;
